<script lang="ts" setup>
import { computed, onMounted, ref, toRef, watch } from "vue";
import type { Config } from "datatables.net";
import DataView from "@/components/DataView.vue";
import KeysetPager from "@/components/KeysetPager.vue";
import TransactionDetailView from "./TransactionDetailView.vue";
import { useBootstrapBreakpoint } from "@/composables/useBootstrapBreakpoint";
import { useRunWorkflowVersion, useServiceTransactions, useTransactionDetail } from "@/queries/runs";
import type { TransactionSummary } from "@/types";
import { formatTimestamp, statusBadge, statusLabel } from "../utils/transactionFormatters";

const props = defineProps<{
  runId: string;
  serviceId: string;
  /** How many transactions the service has, for the "of N" on the range. Passed in rather than counted:
   * counting them costs a read of every one, and the number is already on the card this opened from. */
  total: number;
  /** Service names by id, used until the run's pinned services arrive. */
  serviceNames?: Record<string, string>;
}>();

const pageLength = ref(10);
const status = ref<string | null>(null);
const outcomes = ["Succeeded", "SucceededWithWarning", "Failed", "NeverResponded"];

// From Bootstrap's lg up the list sits beside the detail; below it one pane shows at a time.
const isBelowLg = useBootstrapBreakpoint("lg");
const split = computed(() => !isBelowLg.value);

// Keyset paging. `starts` is where each page began, so going back means asking for a page again rather
// than counting rows backwards — the store returns rows in ascending id order and nothing else.
const starts = ref<string[]>([""]);
const after = computed(() => starts.value[starts.value.length - 1]);

const selectedId = ref<string | null>(null);

const { transactions, transactionsNext, transactionsFetching } = useServiceTransactions(
  toRef(props, "runId"),
  toRef(props, "serviceId"),
  after,
  pageLength,
  status,
);

const { transactionDetail, transactionDetailFetching } = useTransactionDetail(
  toRef(props, "runId"),
  toRef(props, "serviceId"),
  selectedId,
);

// The services of the workflow version the run pinned. The detail reads this service's authored kind from them,
// and the names of the services a transaction refers to.
const { runWorkflowVersion } = useRunWorkflowVersion(toRef(props, "runId"));
const pinnedServices = computed(() => runWorkflowVersion.value?.services);
const service = computed(() => pinnedServices.value?.find((s) => s.id === props.serviceId));
const pinnedServiceNames = computed(() => {
  if (!pinnedServices.value) return props.serviceNames;
  return Object.fromEntries(pinnedServices.value.map((s) => [s.id, s.name]));
});

// --- Pager -----------------------------------------------------------------
const page = computed(() => starts.value.length);
const canPrev = computed(() => starts.value.length > 1);
const canNext = computed(() => !!transactionsNext.value);
function prev() {
  if (canPrev.value) starts.value = starts.value.slice(0, -1);
}
function next() {
  if (transactionsNext.value) starts.value = [...starts.value, transactionsNext.value];
}
const rangeFrom = computed(() =>
  transactions.value.length === 0 ? 0 : (page.value - 1) * pageLength.value + 1,
);
const rangeTo = computed(() => rangeFrom.value + transactions.value.length - 1);
// A filtered list is counted only as far as it has been read, so it has no "of N".
const rangeLabel = computed(() =>
  status.value
    ? `${rangeFrom.value}–${rangeTo.value}`
    : `${rangeFrom.value}–${rangeTo.value} of ${props.total.toLocaleString()}`,
);

const selectedIndex = computed(() => transactions.value.findIndex((t) => t.id === selectedId.value));
const lastIndex = computed(() => transactions.value.length - 1);

// Prev/next walk the loaded page; at either edge they turn the page and land on its far row. `pendingEdge`
// remembers which end to land on once the new page arrives.
const pendingEdge = ref<"first" | "last" | null>(null);
function selectPrev() {
  if (selectedIndex.value > 0) {
    selectedId.value = transactions.value[selectedIndex.value - 1].id;
  } else if (canPrev.value) {
    pendingEdge.value = "last";
    prev();
  }
}
function selectNext() {
  if (selectedIndex.value < lastIndex.value) {
    selectedId.value = transactions.value[selectedIndex.value + 1].id;
  } else if (canNext.value) {
    pendingEdge.value = "first";
    next();
  }
}
watch(transactions, (rows) => {
  if (!pendingEdge.value) {
    selectFirstWhenNoneOnPage();
    return;
  }
  // An empty page has no row to land on. Clearing the edge stops a later refetch from moving the selection.
  if (rows.length === 0) {
    pendingEdge.value = null;
    return;
  }
  selectedId.value = pendingEdge.value === "first" ? rows[0].id : rows[rows.length - 1].id;
  pendingEdge.value = null;
});

// Beside the list the detail always shows a row of the page on screen: the first one, unless one on the page is
// already selected. One pane at a time leaves the list showing until a row is clicked.
function selectFirstWhenNoneOnPage() {
  if (!split.value || selectedIndex.value >= 0) return;
  selectedId.value = transactions.value[0]?.id ?? null;
}
// On mount the breakpoint has been read, so a page already cached when the panel opens is selected here.
onMounted(selectFirstWhenNoneOnPage);
watch(split, selectFirstWhenNoneOnPage);

// A different set of rows is a different first page. Without this the cursor from the unfiltered list
// would carry over and the filtered list would open partway down.
function filterBy(wanted: string | null) {
  status.value = wanted;
  starts.value = [""];
}

// --- Table -----------------------------------------------------------------
// The row's position in the service.
const numberColumn = {
  data: null,
  title: "#",
  render: (_data: unknown, _type: string, _row: TransactionSummary, meta: { row: number }) =>
    `<span class="text-body-secondary">${rangeFrom.value + meta.row}</span>`,
};
const createdColumn = { data: "created", title: "Created", render: (d: string) => formatTimestamp(d) };
const tableOptions = computed(
  () =>
    ({
      columns: split.value
        ? [
            numberColumn,
            createdColumn,
            { data: "status", title: '<span class="visually-hidden">Status</span>', render: "#dot", className: "text-end" },
          ]
        : [
            numberColumn,
            createdColumn,
            { data: "status", title: "Status", render: "#status" },
            { data: "errorMessage", title: "Error", render: "#error" },
          ],
      layout: { topEnd: "", bottomStart: "", bottomEnd: "" },
      paging: false,
      ordering: false,
    }) as Config,
);

function onRowClick(row: TransactionSummary) {
  selectedId.value = row.id;
}

function backToList() {
  selectedId.value = null;
}
</script>

<template>
  <div :class="{ row: split }">
    <!-- LIST: always beside the detail, otherwise only while no transaction is open. -->
    <div v-if="split || !selectedId" :class="{ 'col-lg-4 border-end min-w-0': split }">
      <div v-if="transactionsFetching && transactions.length === 0" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading…</span>
        </div>
      </div>

      <select
        v-if="split"
        class="form-select form-select-sm mb-2"
        aria-label="Outcome"
        :value="status ?? ''"
        :disabled="transactionsFetching"
        @change="filterBy(($event.target as HTMLSelectElement).value || null)"
      >
        <option value="">All outcomes</option>
        <option v-for="option in outcomes" :key="option" :value="option">{{ statusLabel(option) }}</option>
      </select>
      <div v-else class="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <small class="text-body-tertiary">Show</small>
        <button
          v-for="option in outcomes"
          :key="option"
          type="button"
          class="btn btn-sm py-0"
          :class="status === option ? statusBadge(option).replace('bg-', 'btn-') : 'btn-outline-secondary'"
          :disabled="transactionsFetching"
          @click="filterBy(status === option ? null : option)"
        >
          {{ statusLabel(option) }}
        </button>
        <!-- Narrowing reads the whole service, where an unfiltered page reads only itself. Said here so a
             wait for one and not the other reads as the cost it is. -->
        <small v-if="status" class="text-body-tertiary">searches every transaction</small>
      </div>

      <div v-if="transactions.length === 0 && !transactionsFetching" class="text-muted text-center py-5">
        <i class="bi bi-inbox display-6 d-block mb-2"></i>
        <p class="mb-0">{{ status ? "No transactions with that outcome." : "No transactions for this step." }}</p>
      </div>

      <template v-else>
        <!-- DataTables reads its options only when the table is created, so a change of layout remounts it
             with that layout's columns. -->
        <DataView
          :key="split ? 'split' : 'one-pane'"
          :data="transactions"
          :view-mode="'table'"
          :options="tableOptions"
          :selected-row-id="selectedId"
          @row-click="onRowClick"
        >
          <template #dot="{ rowData }">
            <i
              class="bi bi-circle-fill"
              style="font-size: 0.5rem"
              :class="statusBadge(rowData.status).replace('bg-', 'text-')"
              :title="statusLabel(rowData.status)"
            ></i>
            <span class="visually-hidden">{{ statusLabel(rowData.status) }}</span>
          </template>
          <template #status="{ rowData }">
            <span class="badge" :class="statusBadge(rowData.status)">{{ statusLabel(rowData.status) }}</span>
          </template>
          <template #error="{ rowData }">
            <span class="d-inline-block text-truncate text-danger" style="max-width: 240px" :title="rowData.errorMessage ?? ''">
              {{ rowData.errorMessage ?? "" }}
            </span>
          </template>
        </DataView>

        <KeysetPager
          :label="rangeLabel"
          :can-prev="canPrev"
          :can-next="canNext"
          :fetching="transactionsFetching"
          @prev="prev"
          @next="next"
        />
      </template>
    </div>

    <!-- DETAIL -->
    <div v-if="selectedId" :class="{ 'col-lg-8 min-w-0': split }">
      <div v-if="transactionDetailFetching && !transactionDetail" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading…</span>
        </div>
      </div>

      <!-- Keyed by transaction so each one gets a fresh view: only Result open, and no page read held over from
           the transaction before it. -->
      <TransactionDetailView
        v-else-if="transactionDetail"
        :key="transactionDetail.id"
        :run-id="runId"
        :service-id="serviceId"
        :detail="transactionDetail"
        :split="split"
        :can-prev="selectedIndex > 0 || canPrev"
        :can-next="selectedIndex < lastIndex || canNext"
        :service="service"
        :service-names="pinnedServiceNames"
        @back="backToList"
        @prev="selectPrev"
        @next="selectNext"
      />
    </div>
  </div>
</template>
